import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import { Box, Button, FormControlLabel, Grid, IconButton, Switch, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { MuiColorInput } from 'mui-color-input';
import { useEffect, useState } from 'react';
import { GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import GeneralDriverTextFiled from 'src/components/general/GeneralDriverTextFiled';
import GeneralMediaUploader from 'src/components/general/GeneralMediaUploader';
import { FormProvider } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { APP_SECTION_LIST } from 'src/constants/queryKeys';
import { networkCreateAppSection, networkUpdateAppSection } from 'src/network/app-section';
import useFormsModalStore from 'src/store/forms';
import { getDefaultValues } from './defaultValues';

const positionList = [
  { id: 1, title: 'از بالا به پایین' },
  { id: 2, title: 'از پایین به بالا' },
  { id: 3, title: 'از راست به چپ' },
  { id: 4, title: 'از چپ به راست' },
];

const AppSectionForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const appSectionDetails = modal?.data;
  const isEditMode = !!appSectionDetails;

  const [selectedPosition, setSelectedPosition] = useState('');

  const [sectionBackgroundColor, setSectionBackgroundColor] = useState(
    appSectionDetails?.ApplicationSectionBackgroundColor || 'transparent'
  );

  const [sectionBackgroundColorDark, setSectionBackgroundColorDark] = useState(
    appSectionDetails?.ApplicationSectionBackgroundColorDark || 'transparent'
  );

  const [sectionTextColor, setSectionTextColor] = useState(appSectionDetails?.ApplicationSectionTextColor || 'transparent');

  const [sectionTextColorDark, setSectionTextColorDark] = useState(appSectionDetails?.ApplicationSectionTextColorDark || 'transparent');

  const defaultValues = getDefaultValues(appSectionDetails);
  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    trigger,
  } = methods;

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      ApplicationSectionApplicationID: +projectId,
      ApplicationSectionKeyOrder: '',
      ApplicationSectionBackgroundColor: sectionBackgroundColor,
      ApplicationSectionBackgroundColorDark: sectionBackgroundColorDark,
      ApplicationSectionTextColor: sectionTextColor,
      ApplicationSectionTextColorDark: sectionTextColorDark,
      ApplicationSectionStatus: values?.ApplicationSectionStatus ? 1 : 0,
      ApplicationSectionBackgroundRepeat: values?.ApplicationSectionBackgroundRepeat ? 1 : 0,
      ApplicationSectionBackgroundPosition: selectedPosition,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateAppSection(appSectionDetails?.ApplicationSectionID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('بخش موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [APP_SECTION_LIST, projectId], refetchType: 'all' });
          }
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateAppSection(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('بخش موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [APP_SECTION_LIST, projectId], refetchType: 'all' });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setSelectedPosition(appSectionDetails?.ApplicationSectionBackgroundPosition || positionList[0]?.id);
  }, []);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <GeneralDriverTextFiled
                helperDesc={`عنوان بخش رو اینجا وارد کن. این عنوان به عنوان نام یا توضیح کلی بخش در سیستم نمایش داده می‌شه و کاربران یا مدیران بر اساس این عنوان می‌تونن بخش رو شناسایی کنن.`}
                name="ApplicationSectionLable"
                type="text"
                label="عنوان"
              />

              <GeneralSelectMenu
                label="پوزیشن پس زمینه"
                name="ApplicationSectionBackgroundPosition"
                options={positionList}
                value={selectedPosition}
                onChangeValue={setSelectedPosition}
              />

              <div dir="ltr" className="w-full relative">
                <MuiColorInput
                  format="hex"
                  label="رنگ پس زمینه در تم روشن"
                  value={sectionBackgroundColor}
                  onChange={(color) => setSectionBackgroundColor(color)}
                  sx={{ textAlign: 'right', width: '100%' }}
                  isAlphaHidden={true}
                  placeholder="رنگ پس زمینه در تم روشن"
                />

                <span className="absolute right-6 top-4">
                  <Tooltip title="حذف کد رنگی">
                    <IconButton onClick={() => setSectionBackgroundColor('transparent')} className="absolute left-0">
                      <Iconify icon={'eva:trash-2-outline'} sx={{ fontSize: '26px', position: 'absolute', right: '0%' }} />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>

              <div dir="ltr" className="w-full relative">
                <MuiColorInput
                  format="hex"
                  label="رنگ پس زمینه در تم تیره"
                  value={sectionBackgroundColorDark}
                  onChange={(color) => setSectionBackgroundColorDark(color)}
                  sx={{ textAlign: 'right', width: '100%' }}
                  isAlphaHidden={true}
                  placeholder="رنگ پس زمینه در تم تیره"
                />

                <span className="absolute right-6 top-4">
                  <Tooltip title="حذف کد رنگی">
                    <IconButton onClick={() => setSectionBackgroundColorDark('transparent')} className="absolute left-0">
                      <Iconify icon={'eva:trash-2-outline'} sx={{ fontSize: '26px', position: 'absolute', right: '0%' }} />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>

              <div dir="ltr" className="w-full relative">
                <MuiColorInput
                  format="hex"
                  label="رنگ متن در تم روشن"
                  value={sectionTextColor}
                  onChange={(color) => setSectionTextColor(color)}
                  sx={{ textAlign: 'right', width: '100%' }}
                  isAlphaHidden={true}
                  placeholder="رنگ متن در تم روشن"
                />

                <span className="absolute right-6 top-4">
                  <Tooltip title="حذف کد رنگی">
                    <IconButton onClick={() => setSectionTextColor('transparent')} className="absolute left-0">
                      <Iconify icon={'eva:trash-2-outline'} sx={{ fontSize: '26px', position: 'absolute', right: '0%' }} />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>

              <div dir="ltr" className="w-full relative">
                <MuiColorInput
                  format="hex"
                  label="رنگ متن در تم تیره"
                  value={sectionTextColorDark}
                  onChange={(color) => setSectionTextColorDark(color)}
                  sx={{ textAlign: 'right', width: '100%' }}
                  isAlphaHidden={true}
                  placeholder="رنگ متن در تم تیره"
                />

                <span className="absolute right-6 top-4">
                  <Tooltip title="حذف کد رنگی">
                    <IconButton onClick={() => setSectionTextColorDark('transparent')} className="absolute left-0">
                      <Iconify icon={'eva:trash-2-outline'} sx={{ fontSize: '26px', position: 'absolute', right: '0%' }} />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>

              <GeneralUpload
                label="تصویر پس زمینه در تم روشن"
                id="application-section-background-image"
                name="ApplicationSectionBackgroundImage"
                afterChangeAction={(res) => {
                  setValue('ApplicationSectionBackgroundImage', res);
                  trigger('ApplicationSectionBackgroundImage');
                }}
              />

              <GeneralUpload
                label="تصویر پس زمینه در تم تیره"
                id="application-section-background-image-dark"
                name="ApplicationSectionBackgroundImageDark"
                afterChangeAction={(res) => {
                  setValue('ApplicationSectionBackgroundImageDark', res);
                  trigger('ApplicationSectionBackgroundImageDark');
                }}
              />

              <GeneralMediaUploader
                label="فایل صوتی برای تم روشن"
                id="application-section-background-voice"
                name="ApplicationSectionBackgroundVoice"
                afterChangeAction={(res) => {
                  setValue('ApplicationSectionBackgroundVoice', res);
                  trigger('ApplicationSectionBackgroundVoice');
                }}
              />

              <GeneralMediaUploader
                label="فایل صوتی برای تم تیره"
                id="application-section-background-voice-dark"
                name="ApplicationSectionBackgroundVoiceDark"
                afterChangeAction={(res) => {
                  setValue('ApplicationSectionBackgroundVoiceDark', res);
                  trigger('ApplicationSectionBackgroundVoiceDark');
                }}
              />

              <FormControlLabel
                name="ApplicationSectionStatus"
                label={'آیا نمایش داده شود؟'}
                control={
                  <Switch
                    checked={methods.watch('ApplicationSectionStatus')}
                    onChange={(e) => {
                      setValue('ApplicationSectionStatus', e.target.checked ? 1 : 0);
                      trigger('ApplicationSectionStatus');
                    }}
                  />
                }
              />

              <FormControlLabel
                name="ApplicationSectionBackgroundRepeat"
                label="آیا پس زمینه تکرار شود؟"
                control={
                  <Switch
                    checked={methods.watch('ApplicationSectionBackgroundRepeat')}
                    onChange={(e) => {
                      setValue('ApplicationSectionBackgroundRepeat', e.target.checked ? 1 : 0);
                      trigger('ApplicationSectionBackgroundRepeat');
                    }}
                  />
                }
              />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default AppSectionForm;
